import {
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  Hand,
  LucideIcon,
} from "lucide-react";
import { getHeyLightFailureReasonLabel } from "../HeyLight/heyLightFailureReasonLabels.ts";
import { SaleOutcome, SalesGateway } from "./SaleRecord.ts";

export const OTHER_SERIES_COLOR = "#a3a29c";

export const SALES_GATEWAY_LABELS: Record<SalesGateway, string> = {
  [SalesGateway.Stripe]: "Stripe",
  [SalesGateway.HeyLight]: "HeyLight",
  [SalesGateway.Bonifico]: "Bonifico",
};

export const SALES_GATEWAY_COLORS: Record<SalesGateway, string> = {
  [SalesGateway.Stripe]: "#2a78d6",
  [SalesGateway.HeyLight]: "#eb6834",
  [SalesGateway.Bonifico]: "#1baf7a",
};

export const CATEGORICAL_SERIES_COLORS = [
  "#2a78d6",
  "#eb6834",
  "#1baf7a",
  "#eda100",
  "#e87ba4",
  "#008300",
  "#6250d6",
];

export const UNKNOWN_KEY = "unknown";

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  "stripe.card": "Carta",
  "stripe.apple_pay": "Apple Pay",
  "stripe.google_pay": "Google Pay",
  "stripe.paypal": "PayPal",
  "stripe.satispay": "Satispay",
  "stripe.revolut_pay": "Revolut Pay",
  "stripe.sepa_debit": "Addebito SEPA",
  "stripe.customer_balance": "Bonifico su Stripe",
  heylight: "Finanziamento HeyLight",
  bonifico: "Bonifico",
  [UNKNOWN_KEY]: "Non indicato",
};

export function paymentMethodLabelOf(paymentMethodKey: string): string {
  return PAYMENT_METHOD_LABELS[paymentMethodKey] ?? paymentMethodKey;
}

export const SINGLE_PAYMENT_KEY = "1";
export const HEYLIGHT_FINANCING_KEY_PREFIX = "heylight:";

export function installmentsLabelOf(installmentsKey: string): string {
  if (installmentsKey.startsWith(HEYLIGHT_FINANCING_KEY_PREFIX)) {
    const term = installmentsKey.slice(HEYLIGHT_FINANCING_KEY_PREFIX.length);
    return term === UNKNOWN_KEY
      ? "HeyLight · durata non indicata"
      : `HeyLight · ${term} rate`;
  }
  if (installmentsKey === UNKNOWN_KEY) return "Non indicato";
  return installmentsKey === SINGLE_PAYMENT_KEY
    ? "Unica soluzione"
    : `${installmentsKey} rate`;
}

export const SALE_OUTCOME_LABELS: Record<SaleOutcome, string> = {
  [SaleOutcome.Completed]: "Conclusi",
  [SaleOutcome.Awaiting]: "In attesa",
  [SaleOutcome.Abandoned]: "Non conclusi",
  [SaleOutcome.Declined]: "Rifiutati o falliti",
  [SaleOutcome.ManuallyHandled]: "Gestiti a mano",
};

export const SALE_OUTCOME_OF_ONE_ORDER_LABELS: Record<SaleOutcome, string> = {
  [SaleOutcome.Completed]: "Concluso",
  [SaleOutcome.Awaiting]: "In attesa",
  [SaleOutcome.Abandoned]: "Non concluso",
  [SaleOutcome.Declined]: "Rifiutato o fallito",
  [SaleOutcome.ManuallyHandled]: "Gestito a mano",
};

export const OUTCOMES_IN_ORDER = [
  SaleOutcome.Completed,
  SaleOutcome.Awaiting,
  SaleOutcome.Abandoned,
  SaleOutcome.Declined,
  SaleOutcome.ManuallyHandled,
];

export const OUTCOME_STYLES: Record<
  SaleOutcome,
  { color: string; Icon: LucideIcon }
> = {
  [SaleOutcome.Completed]: { color: "#0ca30c", Icon: CircleCheck },
  [SaleOutcome.Awaiting]: { color: "#fab219", Icon: Clock },
  [SaleOutcome.Abandoned]: { color: "#a3a29c", Icon: CircleDashed },
  [SaleOutcome.Declined]: { color: "#d03b3b", Icon: CircleX },
  [SaleOutcome.ManuallyHandled]: { color: "#64748b", Icon: Hand },
};

const STRIPE_FAILURE_LABELS: Record<string, string> = {
  card_declined: "Carta rifiutata",
  insufficient_funds: "Fondi insufficienti",
  expired_card: "Carta scaduta",
  incorrect_cvc: "CVC errato",
  incorrect_number: "Numero di carta errato",
  processing_error: "Errore di elaborazione",
  authentication_required: "Autenticazione richiesta",
  payment_intent_authentication_failure:
    "Autenticazione 3D Secure non riuscita",
  payment_method_provider_decline: "Rifiutato dal circuito di pagamento",
};

export function declineReasonLabelOf(
  gateway: SalesGateway,
  declineReason: string | null,
): string {
  if (gateway === SalesGateway.HeyLight)
    return getHeyLightFailureReasonLabel(
      declineReason as Parameters<typeof getHeyLightFailureReasonLabel>[0],
    );
  if (!declineReason) return "Motivo non indicato";
  return (
    STRIPE_FAILURE_LABELS[declineReason] ?? `Codice Stripe ${declineReason}`
  );
}
